import { useState, useEffect } from 'react';
import { sanitizeHtmlBody } from '../security/sanitizeHtmlBody';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Button, Chip, Input, ShellHeader, Textarea } from '@coordio/ui';
import { EmptyState, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { emailTemplateService } from '../services/emailTemplateService';
import type { EmailTemplateDto, EmailTemplatePreview } from '../services/emailTemplateService';
import { useConfirmDialog } from '../components/common/ConfirmDialog';

const TEMPLATE_DESCRIPTIONS: Record<string, string> = {
  'budget-alert': 'Sent when budget thresholds are reached.',
  'key-rotation': 'Sent when API key rotation is due or completed.',
  'security-alert': 'Sent on suspicious activity or security events.',
  'system-update': 'Sent for system maintenance and update notices.',
};

export const EmailTemplatesPage = () => {
  const { currentTenant } = useContextStore();
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();
  const [templates, setTemplates] = useState<EmailTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editKey, setEditKey] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [preview, setPreview] = useState<EmailTemplatePreview | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!currentTenant) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await emailTemplateService.getTemplates(currentTenant.id);
      setTemplates(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [currentTenant?.id]);

  const handleEdit = (t: EmailTemplateDto) => {
    setEditKey(t.templateKey);
    setEditSubject(t.subject);
    setEditBody(t.htmlBody);
    setPreview(null);
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!currentTenant) return;
    setSaving(true);
    try {
      await emailTemplateService.updateTemplate(currentTenant.id, editKey, editSubject, editBody);
      addNotification('Template saved', 'success');
      setEditOpen(false);
      load();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (templateKey: string) => {
    if (!currentTenant) return;
    const ok = await confirm({
      title: 'Reset template',
      message: 'Reset this template to the default? Any customizations will be lost.',
      confirmText: 'Reset',
    });
    if (!ok) return;
    try {
      await emailTemplateService.resetTemplate(currentTenant.id, templateKey);
      addNotification('Template reset to default', 'success');
      load();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to reset template', 'error');
    }
  };

  const handlePreview = async () => {
    try {
      const result = await emailTemplateService.previewTemplate(editKey, {});
      setPreview(result);
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to generate preview', 'error');
    }
  };

  if (!currentTenant) {
    return (
      <div className="email-templates" data-testid="email-templates-form-page">
        <ShellHeader title="Email Templates" description="Notification email templates for this workspace." data-testid="email-templates-header" />
        <PageContainer data-testid="email-templates-page-container">
          <EmptyState
            title="No workspace selected"
            description="Choose a workspace from the user menu to manage its email templates."
            data-testid="email-templates-empty-state"
          />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="email-templates" data-testid="email-templates-form-page">
      <ShellHeader
        title="Email Templates"
        description={`Tenant: ${currentTenant.name}`}
        data-testid="email-templates-header"
      />

      <PageContainer data-testid="email-templates-page-container">
        {error && (
          <InlineErrorPanel
            message={error}
            data-testid="email-templates-error-panel"
            action={<Button type="button" variant="ghost" size="sm" onClick={() => setError(null)} data-testid="dismiss-email-templates-btn">Dismiss</Button>}
          />
        )}

        {loading ? (
          <LoadingSkeleton rows={6} data-testid="email-templates-loading" />
        ) : templates.length === 0 ? (
          <EmptyState
            title="No email templates found"
            description="Email templates will appear here once the system is configured."
            data-testid="email-templates-empty-state"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.map((t) => (
              <div
                key={t.templateKey}
                className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] overflow-hidden"
                data-testid={`email-templates-card-${t.templateKey}`}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-semibold">
                      {t.templateKey}
                    </p>
                    {t.isCustom && <Chip color="blue" label="Custom" />}
                  </div>
                  <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                    {TEMPLATE_DESCRIPTIONS[t.templateKey] ?? 'Email notification template.'}
                  </p>
                  <span className="text-xs font-medium">
                    Subject:
                  </span>
                  <p className="text-sm font-mono mt-0.5">
                    {t.subject}
                  </p>
                </div>
                <div className="px-4 pb-4 flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => handleEdit(t)} data-testid={`edit-email-templates-${t.templateKey}-btn`}>
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReset(t.templateKey)}
                    disabled={!t.isCustom}
                    data-testid={`reset-email-templates-${t.templateKey}-btn`}
                  >
                    Reset to Default
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </PageContainer>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth data-testid="email-templates-edit-dialog">
        <DialogTitle>Edit Template: {editKey}</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-4 pt-1">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Subject</label>
              <Input
                className="w-full"
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                data-testid="email-templates-subject"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">HTML Body</label>
              <Textarea
                className="w-full font-mono text-[0.85rem]"
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                rows={10}
                data-testid="email-templates-body"
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePreview}
                data-testid="email-templates-preview-btn"
              >
                Preview
              </Button>
            </div>
            {preview && (
              <div
                className="p-3 rounded-xl border border-[var(--glass-border)] bg-[var(--color-bg-surface)]"
              >
                <p className="font-semibold text-sm mb-1">
                  Preview
                </p>
                <p className="text-sm font-medium mb-2">
                  Subject: {preview.subject}
                </p>
                <div
                  className="p-2 rounded border border-[var(--glass-border)] bg-white/[0.03] max-h-[300px] overflow-auto [&_img]:max-w-full"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtmlBody(preview.htmlBody) }}
                />
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button type="button" variant="ghost" onClick={() => setEditOpen(false)} data-testid="cancel-email-templates-btn">Cancel</Button>
          <Button
            type="button"
            variant="default"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-email-templates-btn"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
